import React, { useEffect, useState } from "react";
import { Repeat2, Check, X, Loader2, Megaphone, Hand } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { celebrateFrom } from "@/lib/celebrate";

const swapText = (s) => `${s.sheet_title}: ${s.block_text}`;
const refreshBadges = () => window.dispatchEvent(new Event("rpg:badges-refresh"));

async function call(fn, msg, done) {
  try { await fn(); toast.success(msg); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  finally { done?.(); refreshBadges(); }
}

// Member: offer one of my shifts to a teammate, or (board) post it up for grabs.
export function SwapOfferDialog({ block, signupId, board, onClose, onDone }) {
  const [mates, setMates] = useState([]);
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!board) api.get("/volunteers/teammates").then(({ data }) => setMates(data.items || [])).catch(() => {}); }, [board]);
  const send = async () => {
    setSaving(true);
    const req = board ? api.post("/volunteers/board", { signup_id: signupId, note }) : api.post("/volunteers/swaps", { signup_id: signupId, to_member_id: to, note });
    await call(() => req, board ? "Sent to your coach to repost" : "Swap offer sent", () => { setSaving(false); onDone(); onClose(); });
  };
  const Icon = board ? Megaphone : Repeat2;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={board ? "board-post-dialog" : "swap-offer-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Icon className="h-5 w-5 text-teal-600" /> {board ? "Post to open shift board" : "Offer shift swap"}</DialogTitle>
          <DialogDescription>{block}. {board
            ? "Only your coach sees this. Once your coach reposts it to the team you come off the shift, and the first teammate to claim it gets it. Teammates never see your name or note."
            : "Your teammate accepts first, then your coach approves. You stay signed up until it's approved."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!board && <div>
            <Label>Teammate</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="swap-teammate-select"><SelectValue placeholder="Choose a teammate" /></SelectTrigger>
              <SelectContent>{mates.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`swap-teammate-${m.id}`}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          <div><Label>Note <span className="text-slate-400">(optional)</span></Label><Textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={board ? "Only your coach sees this note" : "e.g. I have a family event — thanks!"} data-testid="swap-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={onClose} data-testid="swap-offer-cancel">Cancel</Button>
          <Button className="rounded-full bg-teal-600 hover:bg-teal-700" disabled={(!board && !to) || saving} onClick={send} data-testid={board ? "board-post-send" : "swap-offer-send"}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {board ? "Post shift" : "Send offer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const outgoingLabel = (s) => s.status === "posted" ? "Given up — waiting for your coach"
  : s.status === "open" ? "Reposted to the team"
  : s.status === "offered" ? `Swap offered to ${s.to_member_name}`
  : `${s.to_member_name} ${s.kind === "open" ? "claimed" : "accepted"} — awaiting coach`;

// Member: status chip + withdraw for an active outgoing swap / board post on my shift.
export function OutgoingSwapStatus({ swap, onDone }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={`swap-status-${swap.id}`}>
      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" data-testid={`swap-status-label-${swap.id}`}>{outgoingLabel(swap)}</span>
      <Button size="sm" variant="ghost" className="h-7 rounded-full text-slate-500" onClick={() => call(() => api.delete(`/volunteers/swaps/${swap.id}`), "Swap withdrawn", onDone)} data-testid={`swap-withdraw-${swap.id}`}>Withdraw</Button>
    </div>
  );
}

// Member: swap offers addressed to me.
export function IncomingSwaps({ items, onDone }) {
  const open = items.filter((s) => s.status === "offered" || s.status === "accepted");
  if (!open.length) return null;
  return (
    <Card className="mb-4 border-amber-200 bg-amber-50/50 p-4 shadow-sm" data-testid="swap-incoming">
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-amber-700">Swap offers for you</p>
      <div className="space-y-2">
        {open.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-100 bg-white p-3" data-testid={`swap-incoming-${s.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">{s.from_member_name} → you{s.kind === "open" && <span className="ml-1.5 text-[11px] font-medium text-teal-600">(claimed from open board)</span>}</p>
              <p className="text-xs text-slate-600">{swapText(s)}</p>
              {s.note && <p className="mt-0.5 text-xs italic text-slate-500">“{s.note}”</p>}
            </div>
            {s.status === "offered" ? (
              <div className="flex gap-2">
                <Button size="sm" className="rounded-full bg-teal-600 hover:bg-teal-700" onClick={() => call(() => api.post(`/volunteers/swaps/${s.id}/accept`), "Accepted — waiting for coach approval", onDone)} data-testid={`swap-accept-${s.id}`}><Check className="mr-1 h-4 w-4" /> Accept</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => call(() => api.post(`/volunteers/swaps/${s.id}/decline`), "Swap declined", onDone)} data-testid={`swap-decline-${s.id}`}><X className="mr-1 h-4 w-4" /> Decline</Button>
              </div>
            ) : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600" data-testid={`swap-incoming-awaiting-${s.id}`}>Waiting for coach approval</span>}
          </div>
        ))}
      </div>
    </Card>
  );
}

// Coach: shifts members gave up — only the coach sees who; "Repost to team" frees the spot for the first teammate to claim.
// Coach: reposted shifts nobody has claimed yet (read-only; names visible to the coach only).
export function RepostedOpenShifts({ items }) {
  if (!items.length) return null;
  return (
    <div className="pt-1" data-testid="coach-board-unclaimed">
      <p className="flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-wider text-slate-500"><Megaphone className="h-3.5 w-3.5" /> Reposted — waiting for a teammate</p>
      {items.map((s) => <p key={s.id} className="px-1 text-xs text-slate-500" data-testid={`coach-board-open-${s.id}`}>{swapText(s)} (given up by {s.from_member_name})</p>)}
    </div>
  );
}

export function GivenUpShifts({ items, onDone }) {
  const [busy, setBusy] = useState(null);
  const repost = (s) => { setBusy(s.id); call(() => api.post(`/volunteers/swaps/${s.id}/repost`), "Reposted to the team", () => { setBusy(null); onDone(); }); };
  return (
    <div className="space-y-2" data-testid="given-up-shifts">
      {items.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-teal-100 bg-white p-3" data-testid={`given-up-${s.id}`}>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800" data-testid={`given-up-member-${s.id}`}>{s.from_member_name} gave up a shift</p>
            <p className="text-xs text-slate-600">{swapText(s)}</p>
            {s.note && <p className="mt-0.5 text-xs italic text-slate-500" data-testid={`given-up-note-${s.id}`}>“{s.note}”</p>}
          </div>
          <Button size="sm" className="rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy === s.id} onClick={() => repost(s)} data-testid={`given-up-repost-${s.id}`}>
            {busy === s.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Megaphone className="mr-1 h-4 w-4" />} Repost to team
          </Button>
        </div>
      ))}
    </div>
  );
}

function CoachDeclineDialog({ swap, onClose, onDone }) {
  const [note, setNote] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="swap-coach-decline-dialog">
        <DialogHeader><DialogTitle className="font-heading">{swap.kind === "open" ? "Decline claim" : "Decline swap"}</DialogTitle><DialogDescription>{swap.from_member_name} → {swap.to_member_name} · {swapText(swap)}{swap.kind === "open" && ". The shift goes back to you to repost."}</DialogDescription></DialogHeader>
        <div><Label>Note <span className="text-slate-400">(optional)</span></Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="swap-coach-decline-note" /></div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full bg-red-600 hover:bg-red-700" onClick={() => call(() => api.post(`/volunteers/swaps/${swap.id}/coach-decline`, { note }), "Swap declined", () => { onDone(); onClose(); })} data-testid="swap-coach-decline-confirm">Decline</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Coach: swaps waiting for approval (and ones still waiting on the teammate).
export function CoachSwapRequests({ data, onDone }) {
  const [declining, setDeclining] = useState(null);
  const { awaiting_coach: waiting = [], awaiting_teammate: offered = [], open_board: board = [], given_up: givenUp = [] } = data || {};
  if (!waiting.length && !offered.length && !board.length && !givenUp.length) return null;
  return (
    <Card className="mb-4 max-w-4xl border-amber-200 bg-amber-50/40 p-4 shadow-sm" data-testid="coach-swap-requests">
      <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-700"><Repeat2 className="h-4 w-4" /> Swap requests</p>
      <div className="space-y-2">
        {givenUp.length > 0 && <GivenUpShifts items={givenUp} onDone={onDone} />}
        {waiting.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-100 bg-white p-3" data-testid={`coach-swap-${s.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">{s.from_member_name} → {s.to_member_name}{s.kind === "open" && <span className="ml-1.5 text-[11px] font-medium text-teal-600" data-testid={`coach-swap-board-tag-${s.id}`}>(claimed from open board)</span>}</p>
              <p className="text-xs text-slate-600">{swapText(s)}</p>
              {s.note && <p className="mt-0.5 text-xs italic text-slate-500">“{s.note}”</p>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => call(() => api.post(`/volunteers/swaps/${s.id}/approve`), "Swap approved", onDone)} data-testid={`coach-swap-approve-${s.id}`}><Check className="mr-1 h-4 w-4" /> Approve</Button>
              <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => setDeclining(s)} data-testid={`coach-swap-decline-${s.id}`}><X className="mr-1 h-4 w-4" /> Decline</Button>
            </div>
          </div>
        ))}
        {offered.map((s) => (
          <p key={s.id} className="px-1 text-xs text-slate-500" data-testid={`coach-swap-offered-${s.id}`}>{s.from_member_name} offered {swapText(s)} to {s.to_member_name} — waiting on teammate.</p>
        ))}
        <RepostedOpenShifts items={board} />
      </div>
      {declining && <CoachDeclineDialog swap={declining} onClose={() => setDeclining(null)} onDone={onDone} />}
    </Card>
  );
}

// Member: teammates' shifts posted up for grabs.
export function OpenShiftBoard({ items, onDone }) {
  const [busy, setBusy] = useState(null);
  const claim = (s) => { setBusy(s.id); call(async () => celebrateFrom((await api.post(`/volunteers/board/${s.id}/claim`)).data), "You're signed up — thank you!", () => { setBusy(null); onDone(); }); };
  return (
    <Card className="mb-4 border-teal-200 bg-white p-4 shadow-sm" data-testid="open-shift-board">
      <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-teal-700"><Megaphone className="h-4 w-4" /> Open shift board</p>
      {items.length === 0 ? <p className="text-sm text-slate-500" data-testid="open-board-empty">No shifts up for grabs right now.</p> : (
        <div className="space-y-2">
          {items.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-teal-100 bg-teal-50/40 p-3" data-testid={`open-board-item-${s.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800">{swapText(s)}</p>
                <p className="text-xs text-slate-600">Open spot · first to claim gets it</p>
              </div>
              <Button size="sm" className="rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy === s.id} onClick={() => claim(s)} data-testid={`open-board-claim-${s.id}`}>
                {busy === s.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Hand className="mr-1 h-4 w-4" />} Claim
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
